/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
import React, { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress } from '@mui/material';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { isEmpty } from 'lodash';
import { useSelector, dispatch } from 'src/redux/store';
import { followUpAppointmentHandler, updateBookingSteps, clearLead } from 'src/redux/slices/lead';
import { getCallScriptSection } from 'src/redux/slices/callScript';
import { callScriptButtonStyle } from 'src/styles/FollowUp';
import { useResponsive, useToggle } from 'src/hooks';
import { setCurrentTreatmentDataGlobal } from 'src/redux/slices/multiTreatment';
import { useForm } from 'react-hook-form';
import CallScriptContainer from '../fullScreenPopUps/CallScriptContainer';
import { Iconify } from '..';

const TCOScript = forwardRef(({ handleNext, handleClose }, ref) => {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { followLead, tableRowLead } = useSelector((state) => state.lead);
    const [outcome, setOutcome] = useState('');
    const { enqueueSnackbar } = useSnackbar();
    const [callScriptActive, setCallScriptActive] = useState(false);
    const { callScriptData, isLoading: isCallScriptLoading } = useSelector((state) => state.callScript);
    const [leadId, setLeadId] = useState('');
    const [treatmentId, setTreatmentId] = useState('');
    const [callScriptReloadPayLoad, setCallScriptReloadPayLoad] = useState({});
    const defaultValues = {
        state: 0
    };

    const { currentTreatmentDataGlobal } = useSelector((state) => state.multiTreatment);

    useEffect(() => {
        if (practiceId) {
            dispatch(getCallScriptSection({ practiceId, treatment: getTreatmentName(tableRowLead && tableRowLead.treatment_name) || '', isTemplate: false, category: 'TCO' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId]);

    // Once TCO scripts are known to exist for this treatment, remember it locally.
    // state.callScript (Redux) gets cleared when the script panel closes, which
    // otherwise makes the "Open TCO Scripts" button disappear on reopen.
    const [hasScripts, setHasScripts] = useState(false);
    useEffect(() => {
        if (callScriptData?.results?.length > 0) setHasScripts(true);
    }, [callScriptData]);

    const methods = useForm({
        defaultValues,
    });

    const onSubmit = (data) => {
        if (data.state > 0) {
            handleNext(true);
        }
        if (data.state < 1) {
            enqueueSnackbar('Please click one of outcome', {
                variant: 'warning',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                }
            });
        }
    };

    const {
        handleSubmit,
        setValue,
    } = methods;

    useImperativeHandle(
        ref,
        () => ({
            async submit() {
                await handleSubmit(onSubmit)();
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const isMobile = useResponsive('up', 'sm');

    const appoimentHandler = (data) => {
        setValue('state', 1);
        dispatch(followUpAppointmentHandler(data));
        setOutcome(data);
        dispatch(updateBookingSteps(data));
    };

    const openCallScripts = () => {
        setCallScriptActive(true);
    };

    const {
        toggle: isAppointmentNotBooked,
        onOpen: onOpenAppointmentNotBooked,
        onClose: onCloseAppointmentNotBooked
    } = useToggle();

    const {
        toggle: isAppointmentBooked,
        onOpen: onOpenAppointmentBooked,
        onClose: onCloseAppointmentBooked
    } = useToggle();

    const onOpenAppointmentNotBookedEvent = (data) => {
        setValue('state', 1);
        onOpenAppointmentNotBooked();
        setCallScriptReloadPayLoad(data);
    };

    const onOpenAppointmentBookedEvent = (data) => {
        setValue('state', 1);
        onOpenAppointmentBooked();
        setCallScriptReloadPayLoad(data);
    };

    const appointmentNotBookedClose = () => {
        onCloseAppointmentNotBooked();
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
        handleClose('finish', callScriptReloadPayLoad);
    };

    const onAppointmentNotBookClose = () => {
        onCloseAppointmentNotBooked();
    };

    const appointmentBookedClose = () => {
        onCloseAppointmentBooked();
        dispatch(clearLead());
        handleClose('finish', callScriptReloadPayLoad);
    };

    const onAppointmentBookClose = () => {
        onCloseAppointmentBooked();
    };

    const getTreatmentName = (name) => {
        if (!isEmpty(currentTreatmentDataGlobal?.treatmentData)) return currentTreatmentDataGlobal?.treatmentData?.treatment;
        return name;
    };

    const handleFinalClose = (reason, reloadPayload) => {
        if (reason === 'cancel') {
            setCallScriptActive(false);
            return;
        }
        // Completing the TCO script IS a valid outcome — set state so the host
        // Finish gate ("Please click one of outcome") isn't tripped if the host
        // submits this form's ref while the script panel is still mounted.
        setValue('state', 1);
        handleClose("tco", reloadPayload);
        setCallScriptActive(false);
    };

    return (
        <>
            {/* Flat inline button — no card wrapper — so it lines up with Finish in
                the centered action bar. CallScriptContainer below is a separate
                full-screen overlay, so nothing else needs the old Card. */}
            {isCallScriptLoading ? (
                <Button sx={{ ...callScriptButtonStyle, width: "auto", minHeight: 0, height: "38px", py: 0.5, fontSize: "13px", display: "flex", alignItems: "center" }}>
                    <Iconify style={{ marginRight: '10px', width: 24, height: 24 }} icon={'eva:menu-2-fill'} /> TCO Scripts Loading <Box sx={{ ml: "1rem", display: "flex", alignItems: "center" }}><CircularProgress size="1rem" /></Box>
                </Button>
            ) : hasScripts && (followLead?.id || tableRowLead?.id) ? (
                <Button sx={{ ...callScriptButtonStyle, width: "auto", minHeight: 0, height: "38px", py: 0.5, fontSize: "13px", display: "flex", alignItems: "center", background: "#5119B7", color: "white", "&:hover": { background: "#3d0f8a", color: "white" } }} onClick={() => openCallScripts()}>
                    <Iconify style={{ marginRight: '10px', width: 24, height: 24 }} icon={'eva:menu-2-fill'} /> Open TCO Scripts
                </Button>
            ) : null}
            <CallScriptContainer
                open={callScriptActive}
                onClose={handleFinalClose}
                scriptList={callScriptData?.results}
                followLead={followLead || tableRowLead}
                onOpenAppointmentNotBooked={onOpenAppointmentNotBookedEvent}
                onOpenAppointmentBooked={onOpenAppointmentBookedEvent}
                setLeadId={setLeadId}
                setTreatmentId={setTreatmentId}
                type="TCO"
                scriptCategory="TCO"
                useV2
                callType="inbound"
            />
        </>
    );
});

TCOScript.propTypes = {
    handleNext: PropTypes.func,
    handleClose: PropTypes.func,
};

export default TCOScript;
